<template>
  <v-container fluid class="dashboard-container">
    <!-- CUANDO NO EXISTE -->
    <div v-if="reportNotFound" class="fullcenter">
      <h1 class="error-title">Ups! Reporte no encontrado</h1>
      <p class="error-subtitle">
        El dashboard solicitado no existe o no está configurado.<br />
        Verifica la URL o vuelve a la página de Dashboards.
      </p>
    </div>

    <!-- CUANDO SÍ EXISTE -->
    <div v-else>
      <v-row no-gutters>
        <v-col cols="12">
          <v-card outlined class="report-card">
            <div class="powerbi-outer" :class="{ 'powerbi-fullscreen': isFullscreen }">
              <div class="powerbi-wrapper" :style="wrapperStyle" ref="fullscreenContainer">
                <v-card class="report-card" outlined>
                  <div ref="reportContainer" :class="['powerbi-container', { 'powerbi-container--no-cut': !shouldApplyCut }]"></div>

                  <div v-if="loading" class="overlay">
                    <v-progress-circular indeterminate size="40" />
                  </div>
                </v-card>
              </div>
            </div>

            <div v-if="error" class="mt-2 red--text text-caption">
              {{ error }}
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import * as pbi from "powerbi-client";
import { eventBus } from "@/helpers/eventBus";

export default {
  name: "DashboardsPowerBIView",

  data() {
    return {
      report: null,
      loading: true,
      error: null,
      reportNotFound: false,
      powerbiService: null,
      workspaceId: null,

      // Relación del reporte
      reportAspectRatio: "16 / 9",
      reportMaxWidth: 1200,

      // rotación automática
      pages: [],
      currentPageIndex: 0,
      autoRotateInterval: null,

      // estado del toggle (persistido)
      autoRotateEnabled: JSON.parse(localStorage.getItem("autoRotateEnabled") ?? "true"),
      
      // pantalla completa
      isFullscreen: false,
    };
  },

  computed: {
    wrapperStyle() {
      return {
        "--report-aspect": this.reportAspectRatio,
        "--report-maxw": `${this.reportMaxWidth}px`,
      };
    },
    shouldApplyCut() {
      return this.workspaceId && this.workspaceId.toLowerCase() !== "9f6e3fea-1a7b-403e-9226-b21766135ed2";
    },
  },

  watch: {
    "$route.params": {
      deep: true,
      handler() {
        this.loadFromRoute();
      },
    },
  },

  mounted() {
    this.powerbiService = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory
    );

    // escuchar cambios del switch
    eventBus.$on("auto-rotate-toggle", this.onAutoRotateToggle);
    
    // escuchar toggle desde App.vue
    eventBus.$on("toggle-fullscreen", this.toggleFullscreen);

    // listener para pantalla completa
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", this.handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", this.handleFullscreenChange);

    // cargar reporte
    this.loadFromRoute();
  },

  beforeDestroy() {
    eventBus.$off("auto-rotate-toggle", this.onAutoRotateToggle);
    eventBus.$off("toggle-fullscreen", this.toggleFullscreen);
    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("mozfullscreenchange", this.handleFullscreenChange);
    document.removeEventListener("MSFullscreenChange", this.handleFullscreenChange);
    this.stopAutoRotate();
    this.resetEmbed();
    if (this.isFullscreen) {
      this.exitFullscreen();
    }
  },

  methods: {
    onAutoRotateToggle(val) {
      this.autoRotateEnabled = !!val;
      localStorage.setItem("autoRotateEnabled", JSON.stringify(this.autoRotateEnabled));
      this.applyAutoRotate(this.autoRotateEnabled);
    },

    // prende/apaga rotación según estado real
    applyAutoRotate(enabled) {
      if (!enabled) {
        this.stopAutoRotate();
        return;
      }

      // si está encendido y ya tengo páginas, rotar
      if (this.pages && this.pages.length > 0) {
        this.startAutoRotate();
      }
    },

    resetEmbed() {
      this.stopAutoRotate();

      if (this.report) {
        this.report.off("loaded");
        this.report.off("error");
      }

      if (this.powerbiService && this.$refs.reportContainer) {
        this.powerbiService.reset(this.$refs.reportContainer);
      }

      this.report = null;
      this.pages = [];
      this.currentPageIndex = 0;
    },

    stopAutoRotate() {
      if (this.autoRotateInterval) {
        clearInterval(this.autoRotateInterval);
        this.autoRotateInterval = null;
      }
    },

    startAutoRotate() {
      if (!this.pages.length) return;
      if (!this.autoRotateEnabled) return;

      // evita intervalos duplicados
      this.stopAutoRotate();

      this.autoRotateInterval = setInterval(async () => {
        if (!this.autoRotateEnabled) {
          this.stopAutoRotate();
          return;
        }

        try {
          const nextIndex = (this.currentPageIndex + 1) % this.pages.length;
          if (nextIndex === this.currentPageIndex) return;

          this.currentPageIndex = nextIndex;
          await this.pages[this.currentPageIndex].setActive();
        } catch (err) {
          console.error("Error rotando página Power BI:", err);
        }
      }, 20000);
    },

    async loadFromRoute() {
      const id = this.$route.params.name;

      this.loading = true;
      this.error = null;
      this.reportNotFound = false;
      this.resetEmbed();

      if (!id) {
        this.reportNotFound = true;
        this.loading = false;
        return;
      }

      try {
        const API_BASE = process.env.VUE_APP_API_BASE;
        
        // Obtener workspace_id primero
        const dashboardInfo = await axios.get(`${API_BASE}/dashboard/powerbi/${encodeURIComponent(id)}`);
        this.workspaceId = dashboardInfo.data?.workspace_id || null;
        
        const { data } = await axios.get(`${API_BASE}/dashboard/embed/${encodeURIComponent(id)}`);

        // esperado: { embedUrl, embedToken, reportId }
        const container = this.$refs.reportContainer;
        if (!container) throw new Error("No reportContainer");

        const config = {
          type: "report",
          tokenType: pbi.models.TokenType.Embed,
          accessToken: data.embedToken,
          embedUrl: data.embedUrl,
          id: data.reportId,
          permissions: pbi.models.Permissions.Read,
          settings: {
            panes: {
              filters: { visible: false },
              pageNavigation: { visible: true },
            },
            navContentPaneEnabled: true,
            background: pbi.models.BackgroundType.Transparent,
          },
        };

        this.report = this.powerbiService.embed(container, config);

        this.report.on("loaded", async () => {
          this.loading = false;

          // Solo páginas visibles (0 = AlwaysVisible, 1 = HiddenInViewMode en Power BI)
          const allPages = await this.report.getPages();
          this.pages = allPages.filter((page) => page.visibility === 0);

          // Índice inicial: página activa dentro de las visibles, o 0
          const activePage = await this.report.getActivePage();
          const activeIndex = this.pages.findIndex((p) => p.name === activePage?.name);
          this.currentPageIndex = activeIndex >= 0 ? activeIndex : 0;

          // aplicar toggle actual
          this.applyAutoRotate(this.autoRotateEnabled);
        });

        this.report.on("error", (e) => {
          console.error("PowerBI error:", e?.detail || e);
          this.error = "Error al cargar el dashboard.";
          this.loading = false;
        });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          this.reportNotFound = true;
          this.error = null;
        } else {
          console.error("Error al cargar embed:", err?.response?.data || err);
          this.error = "Error al cargar el dashboard de Power BI.";
        }
        this.loading = false;
      }
    },

    toggleFullscreen() {
      if (!this.$refs.fullscreenContainer) return;

      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    },

    enterFullscreen() {
      const element = this.$refs.fullscreenContainer;
      if (!element) return;

      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    },

    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },

    handleFullscreenChange() {
      const isFullscreenNow = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      this.isFullscreen = isFullscreenNow;
      // Notificar a App.vue el cambio de estado
      eventBus.$emit("fullscreen-changed", isFullscreenNow);
    },
  }
};
</script>

<style scoped>
/* =========================
   CONTENEDOR GENERAL
   ========================= */
.dashboard-container {
  margin: 3vh auto;
  padding: 3px;
  width: 80%;
  max-width: 1500px;
}

.report-card {
  width: 100%;
  height: 100%;
  padding: 0;
  border-radius: 10px;
}

/* =========================
   ESTADO ERROR
   ========================= */
.fullcenter {
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: #1f2937;
  border-radius: 12px;
  padding: 32px 16px;
}

.error-title {
  font-size: 42px;
  font-weight: 900;
  color: #ffffff;
}

.error-subtitle {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 12px;
}

/* =========================
   POWER BI CONTENEDOR
   ========================= */
.powerbi-outer {
  display: flex;
  justify-content: center;
  width: 100%;
  position: relative;
}

.powerbi-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.powerbi-fullscreen .powerbi-wrapper {
  max-width: 100%;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border-radius: 0;
  aspect-ratio: unset;
}


/* MARCO DEL REPORTE */
.powerbi-wrapper {
  position: relative;
  margin: 12px auto;
  width: 100%;
  max-width: 1600px;
  aspect-ratio: var(--report-aspect, 16 / 9);
  overflow: hidden;
  padding: 10px;
  border-radius: 14px;
}

/* FONDO CLARO POR DEFECTO */
.powerbi-wrapper::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.35);
  z-index: 0;
  border-radius: 14px;
}


/* =========================
   CONTENEDOR DEL IFRAME
   ========================= */
.powerbi-container {
  position: absolute;
  inset: 0;
  top: -48px;
  /* corta la barra superior del reporte */
  height: calc(100% + 48px);
  width: 100%;
  z-index: 1;
  /* SOBRE el fondo */
}

.powerbi-container--no-cut {
  top: 0;
  height: 100%;
}

/* iframe Power BI */
.powerbi-container iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #ffffff;
  /* evita transparencias raras */
}

/* =========================
   FALLBACK SIN aspect-ratio
   ========================= */
@supports not (aspect-ratio: 16 / 9) {
  .powerbi-wrapper {
    height: 0;
    padding-top: 56.25%;
  }
}

/* =========================
   OVERLAY DE CARGA
   ========================= */
.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.08);
  z-index: 2;
}
</style>